(() => {
  const roots = new Map();

  const parseVariants = (root) => {
    const script = root.querySelector('[data-lmc-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[LMC product] Could not parse variants.', error);
      return [];
    }
  };

  const setThemeHeaderState = () => {
    const productRoots = [...document.querySelectorAll('[data-lmc-product]')];
    document.body.classList.toggle(
      'lmc-product-hide-theme-header',
      productRoots.some((root) => root.dataset.hideThemeHeader === 'true')
    );
  };

  const lockPage = (state) => {
    if (state.locked) return;
    state.scrollY = window.scrollY;
    state.previousBodyStyles = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('lmc-product-lock');
    state.locked = true;
  };

  const unlockPage = (state) => {
    if (!state.locked) return;
    const previous = state.previousBodyStyles || {};
    document.body.style.position = previous.position || '';
    document.body.style.top = previous.top || '';
    document.body.style.width = previous.width || '';
    document.body.style.overflow = previous.overflow || '';
    document.body.classList.remove('lmc-product-lock');
    window.scrollTo(0, state.scrollY || 0);
    state.locked = false;
  };

  const openConfig = (state) => {
    state.config.classList.add('is-open');
    state.config.setAttribute('aria-hidden', 'false');
    lockPage(state);
    requestAnimationFrame(() => state.config.querySelector('[data-lmc-config-close]')?.focus());
  };

  const closeConfig = (state) => {
    state.config.classList.remove('is-open');
    state.config.setAttribute('aria-hidden', 'true');
    unlockPage(state);
    state.openButton?.focus();
  };

  const selectedValues = (state) => state.groups.map((group) => {
    const selected = group.querySelector('.lmc-config-option.is-selected');
    return selected?.dataset.lmcOptionValue || '';
  });

  const findVariant = (state, values) => state.variants.find((variant) => {
    if (!Array.isArray(variant.options) || variant.options.length !== values.length) return false;
    return variant.options.every((option, index) => option === values[index]);
  });

  const canSelectValue = (state, optionIndex, value, currentValues) => state.variants.some((variant) => {
    if (!variant.available || !Array.isArray(variant.options)) return false;
    return variant.options.every((optionValue, index) => {
      if (index === optionIndex) return optionValue === value;
      const selected = currentValues[index];
      return !selected || optionValue === selected;
    });
  });

  const updateOptionAvailability = (state) => {
    const values = selectedValues(state);
    state.groups.forEach((group, optionIndex) => {
      group.querySelectorAll('[data-lmc-option-value]').forEach((button) => {
        const available = canSelectValue(state, optionIndex, button.dataset.lmcOptionValue || '', values);
        button.disabled = !available;
        button.setAttribute('aria-disabled', available ? 'false' : 'true');
      });
    });
  };

  const updateVariant = (state) => {
    const values = selectedValues(state);
    const variant = state.groups.length
      ? findVariant(state, values)
      : (state.variants.find((item) => String(item.id) === String(state.variantInput?.value || '')) || state.variants[0]);
    state.currentVariant = variant || null;
    if (state.variantInput) state.variantInput.value = variant?.id || '';
    if (state.submit) {
      state.submit.disabled = !variant || !variant.available;
      state.submit.textContent = variant && variant.available
        ? state.submit.dataset.defaultLabel || 'Add to enquiry list'
        : 'Unavailable';
    }
    const sku = state.root.querySelector('[data-lmc-current-sku]');
    if (sku && variant?.sku) sku.textContent = variant.sku;
    updateOptionAvailability(state);
  };

  const selectOption = (state, button) => {
    if (button.disabled) return;
    const group = button.closest('[data-lmc-option-group]');
    if (!group) return;
    group.querySelectorAll('[data-lmc-option-value]').forEach((candidate) => {
      const selected = candidate === button;
      candidate.classList.toggle('is-selected', selected);
      candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    updateVariant(state);
  };

  const addToEnquiry = (state) => {
    if (!state.currentVariant || !state.currentVariant.available) return;
    const key = 'lmcEnquiryList';
    let items = [];
    try {
      items = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(items)) items = [];
    } catch (error) {
      items = [];
    }

    const entry = {
      productHandle: state.root.dataset.productHandle || '',
      variantId: state.currentVariant.id,
      title: state.currentVariant.name || state.currentVariant.title || '',
      addedAt: Date.now()
    };
    const exists = items.some((item) => String(item.variantId) === String(entry.variantId));
    if (!exists) items.push(entry);
    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch (error) {
      console.warn('[LMC product] Could not save enquiry list.', error);
    }

    state.submit.textContent = 'Added to enquiry list';
    window.setTimeout(() => {
      if (state.submit && state.currentVariant?.available) {
        state.submit.textContent = state.submit.dataset.defaultLabel || 'Add to enquiry list';
      }
    }, 1800);
  };

  const initModel = (state) => {
    const stage = state.root.querySelector('[data-lmc-product-stage]');
    if (!stage || stage.dataset.hasModel !== 'true') return;
    const model = stage.querySelector('model-viewer');
    const hint = stage.querySelector('[data-lmc-model-hint]');
    if (!model) return;

    const enableInteraction = () => {
      hint?.classList.add('is-hidden');
      stage.classList.add('is-interactive');
    };

    model.addEventListener('pointerdown', enableInteraction, { once: true });
    model.addEventListener('touchstart', enableInteraction, { once: true, passive: true });
    window.setTimeout(enableInteraction, 2200);

    let fieldOfView = 35;
    const setZoom = (next) => {
      fieldOfView = Math.min(55, Math.max(12, next));
      try {
        model.fieldOfView = `${fieldOfView}deg`;
      } catch (error) {
        model.setAttribute('field-of-view', `${fieldOfView}deg`);
      }
      enableInteraction();
    };

    stage.querySelector('[data-lmc-zoom-in]')?.addEventListener('click', () => setZoom(fieldOfView - 4));
    stage.querySelector('[data-lmc-zoom-out]')?.addEventListener('click', () => setZoom(fieldOfView + 4));
  };

  const initSwitcher = (state) => {
    const switcher = state.root.querySelector('[data-lmc-product-switcher]');
    const hero = state.root.querySelector('[data-lmc-product-stage]');
    if (!switcher || !hero) return;
    state.switcherObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      switcher.classList.toggle('is-visible', entry.isIntersecting && entry.intersectionRatio > 0.18);
    }, { threshold: [0, 0.18, 0.7] });
    state.switcherObserver.observe(hero);
  };

  const initRoot = (root) => {
    if (roots.has(root)) return;
    const config = root.querySelector('[data-lmc-config]');
    if (!config) return;

    const state = {
      root,
      config,
      variants: parseVariants(root),
      groups: [...root.querySelectorAll('[data-lmc-option-group]')],
      variantInput: root.querySelector('[data-lmc-selected-variant]'),
      submit: root.querySelector('[data-lmc-enquiry-add]'),
      openButton: root.querySelector('[data-lmc-config-open]'),
      currentVariant: null,
      locked: false,
      scrollY: 0
    };

    state.openButton?.addEventListener('click', () => openConfig(state));
    config.querySelectorAll('[data-lmc-config-close]').forEach((button) => button.addEventListener('click', () => closeConfig(state)));
    config.querySelectorAll('[data-lmc-option-value]').forEach((button) => button.addEventListener('click', () => selectOption(state, button)));
    state.submit?.addEventListener('click', () => addToEnquiry(state));

    state.onKeydown = (event) => {
      if (event.key === 'Escape' && config.classList.contains('is-open')) closeConfig(state);
    };
    document.addEventListener('keydown', state.onKeydown);

    updateVariant(state);
    initModel(state);
    initSwitcher(state);
    roots.set(root, state);
    setThemeHeaderState();
  };

  const destroyRoot = (root) => {
    const state = roots.get(root);
    if (!state) return;
    unlockPage(state);
    state.switcherObserver?.disconnect();
    document.removeEventListener('keydown', state.onKeydown);
    roots.delete(root);
    setThemeHeaderState();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-lmc-product]').forEach(initRoot);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-lmc-product]').forEach(destroyRoot);
  });
})();
